import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { apiRequest } from "@/lib/queryClient";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { useToast } from "@/hooks/use-toast";
import { 
  LayoutDashboard, Users, Building2, FileText, ListTodo, 
  Shield, Settings, Bell, Search, AlertTriangle, CheckCircle2, 
  Clock, TrendingUp, Activity, Download, Filter, Plus, X, Key, Copy, Eye, EyeOff, Trash2, FlaskConical, Play, Mail, Star, MessageSquare, Flag, Ban, BadgeCheck, Loader2, BarChart2
} from "lucide-react";
import MotherAnalytics from "./mother-analytics";

interface OverviewData {
  totalActiveReqs: number;
  recsAtRisk: number;
  recsBreadched: number;
  overloadedAgents: number;
  bottleneckStages: { stage: string; count: number }[];
  slaAttainment: number;
  urgentQueues: number;
}

interface AgentCapacity {
  userId: number;
  fullName: string;
  email: string;
  teamName: string | null;
  activeReqCount: number;
  openTasks: number;
  completedThisMonth: number;
  slaBreaches: number;
  utilization: number;
}

interface Requisition {
  id: number;
  title: string;
  clientName: string;
  mode: string;
  ownerAgentName: string | null;
  ownerTeamName: string | null;
  status: string;
  slaStatus: string;
  createdAt: string;
  candidateCount: number;
}

interface WorkQueueItem {
  id: number;
  taskType: string;
  status: string;
  priority: number;
  title: string;
  description: string | null;
  dueAt: string | null;
  createdAt: string;
}

interface Notification {
  id: number;
  notificationType: string;
  title: string;
  message: string;
  isRead: boolean;
  createdAt: string;
}

interface AdminNotificationRecord {
  id: string;
  type: string;
  payload: Record<string, unknown>;
  read: boolean;
  createdAt: string;
}

interface ApiKeyRecord {
  id: number;
  name: string;
  keyPrefix: string;
  scope: string;
  createdBy: number | null;
  creatorName: string;
  expiresAt: string | null;
  lastUsedAt: string | null;
  isRevoked: boolean;
  createdAt: string;
}

export default function MotherDashboard() {
  const [activeTab, setActiveTab] = useState("overview");
  const [searchQuery, setSearchQuery] = useState("");
  const [modeFilter, setModeFilter] = useState<string>("all");
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const queryClient = useQueryClient();
  const { toast } = useToast();

  // Modal states
  const [showAddTeamModal, setShowAddTeamModal] = useState(false);
  const [showEditSlaModal, setShowEditSlaModal] = useState(false);
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [showNotificationsModal, setShowNotificationsModal] = useState(false);
  const [selectedQueueItem, setSelectedQueueItem] = useState<WorkQueueItem | null>(null);

  // Form states
  const [newTeamName, setNewTeamName] = useState("");
  const [newTeamDescription, setNewTeamDescription] = useState("");
  const [newTeamCapacity, setNewTeamCapacity] = useState("10");
  const [selectedAgentForAssign, setSelectedAgentForAssign] = useState("");

  // SLA form states
  const [slaIntakeHours, setSlaIntakeHours] = useState("24");
  const [slaOutreachHours, setSlaOutreachHours] = useState("48");
  const [slaShortlistDays, setSlaShortlistDays] = useState("7");
  const [slaStaleDays, setSlaStaleDays] = useState("14");

  // API Key form states
  const [showGenerateKeyModal, setShowGenerateKeyModal] = useState(false);
  const [newKeyName, setNewKeyName] = useState("");
  const [newKeyScope, setNewKeyScope] = useState("dossier_sync");
  const [newKeyExpiry, setNewKeyExpiry] = useState("never");
  const [generatedKey, setGeneratedKey] = useState<string | null>(null);
  const [showKey, setShowKey] = useState(false);

  // Sandbox form states
  const [showCreateRunModal, setShowCreateRunModal] = useState(false);
  const [sandboxApplicants, setSandboxApplicants] = useState("500");
  const [sandboxTimeWindow, setSandboxTimeWindow] = useState("60");
  const [sandboxAgentCount, setSandboxAgentCount] = useState("5");
  const [sandboxRoleFamily, setSandboxRoleFamily] = useState("Mixed");
  const [sandboxProcessingModel, setSandboxProcessingModel] = useState("auto");
  const [sandboxAssignmentPolicy, setSandboxAssignmentPolicy] = useState("round_robin");
  const [sandboxStrongPct, setSandboxStrongPct] = useState("30");
  const [sandboxBorderlinePct, setSandboxBorderlinePct] = useState("40");
  const [sandboxDuplicateRate, setSandboxDuplicateRate] = useState("5");
  const [sandboxInvalidRate, setSandboxInvalidRate] = useState("10");
  const [selectedSandboxRun, setSelectedSandboxRun] = useState<string | null>(null);

  const { data: overview } = useQuery<OverviewData>({
    queryKey: ["/api/mother/overview"],
    refetchInterval: 30000
  });

  const { data: agents } = useQuery<AgentCapacity[]>({
    queryKey: ["/api/mother/agents"],
    enabled: activeTab === "agents" || activeTab === "queues" || showAssignModal
  });

  const { data: teams } = useQuery<any[]>({
    queryKey: ["/api/mother/teams"],
    enabled: activeTab === "agents" || activeTab === "settings"
  });

  const { data: clients } = useQuery<any[]>({
    queryKey: ["/api/mother/clients"],
    enabled: activeTab === "clients"
  });

  const { data: requisitionsData } = useQuery<{ requisitions: Requisition[]; total: number }>({
    queryKey: ["/api/mother/requisitions", modeFilter, statusFilter],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (modeFilter !== "all") params.set("mode", modeFilter);
      if (statusFilter !== "all") params.set("status", statusFilter);
      const response = await apiRequest("GET", `/api/mother/requisitions?${params}`);
      return response.json();
    },
    enabled: activeTab === "requisitions"
  });

  const { data: workQueueData } = useQuery<{ items: WorkQueueItem[]; total: number }>({
    queryKey: ["/api/mother/queues"],
    enabled: activeTab === "queues"
  });

  const { data: auditData } = useQuery<{ logs: any[]; total: number }>({
    queryKey: ["/api/mother/audit"],
    enabled: activeTab === "compliance"
  });

  const { data: notifications } = useQuery<Notification[]>({
    queryKey: ["/api/mother/notifications"],
    refetchInterval: 60000
  });

  const { data: adminNotifs, refetch: refetchAdminNotifs } = useQuery<AdminNotificationRecord[]>({
    queryKey: ["/api/mother/admin-notifications"],
    refetchInterval: 60000,
    enabled: activeTab === "overview",
  });

  const markAdminNotifRead = useMutation({
    mutationFn: async (id: string) => {
      await apiRequest("PATCH", `/api/mother/admin-notifications/${id}/read`);
    },
    onSuccess: () => refetchAdminNotifs(),
  });

  const { data: metrics } = useQuery<any>({
    queryKey: ["/api/mother/reports/metrics"],
    enabled: activeTab === "settings"
  });

  const { data: apiKeys, refetch: refetchApiKeys } = useQuery<ApiKeyRecord[]>({
    queryKey: ["/api/api-keys"],
    enabled: activeTab === "apikeys"
  });

  const { data: sandboxStatus } = useQuery<{ sandboxMode: boolean; safetyCheck: { safe: boolean; error?: string }; banner: string }>({
    queryKey: ["/api/sandbox/status"],
    enabled: activeTab === "sandbox",
    retry: false
  });

  const { data: sandboxRuns, refetch: refetchSandboxRuns } = useQuery<{ runs: any[] }>({
    queryKey: ["/api/sandbox/runs"],
    enabled: activeTab === "sandbox" && sandboxStatus?.sandboxMode === true,
    retry: false
  });

  const { data: selectedRunData, refetch: refetchSelectedRun } = useQuery<{ run: any; metrics: any; applicants?: any[] }>({
    queryKey: ["/api/sandbox/runs", selectedSandboxRun],
    queryFn: async () => {
      if (!selectedSandboxRun) return null;
      const response = await apiRequest("GET", `/api/sandbox/runs/${selectedSandboxRun}`);
      return response.json();
    },
    enabled: !!selectedSandboxRun && activeTab === "sandbox"
  });

  const { data: commHealthData } = useQuery<any[]>({
    queryKey: ["/api/mother/communication-health"],
    enabled: activeTab === "comms",
    retry: false
  });

  const [feedbackTypeFilter, setFeedbackTypeFilter] = useState<string>("all");
  const [feedbackPage, setFeedbackPage] = useState(1);
  const { data: feedbackData, isLoading: feedbackLoading } = useQuery<{ feedback: any[]; total: number; averageRating: number | null; wouldRecommendPercent: number; page: number; limit: number }>({
    queryKey: ["/api/mother/feedback", feedbackTypeFilter, feedbackPage],
    queryFn: async () => {
      const params = new URLSearchParams({ page: String(feedbackPage) });
      if (feedbackTypeFilter !== "all") params.set("type", feedbackTypeFilter);
      const res = await apiRequest("GET", `/api/mother/feedback?${params}`);
      if (!res.ok) throw new Error("Failed to fetch feedback");
      return res.json();
    },
    enabled: activeTab === "feedback",
    retry: false
  });

  const { data: flaggedData, isLoading: flaggedLoading, refetch: refetchFlagged } = useQuery({
    queryKey: ["/api/mother/flagged-accounts"],
    queryFn: async () => {
      const res = await apiRequest("GET", "/api/mother/flagged-accounts");
      if (!res.ok) throw new Error("Failed to fetch flagged accounts");
      return res.json();
    },
    enabled: activeTab === "flagged",
    retry: false,
  });

  const unreadNotifications = notifications?.filter(n => !n.isRead) || [];

  // Mutations
  const blockWorkspaceMutation = useMutation({
    mutationFn: async (workspaceId: number) => {
      const res = await apiRequest("PATCH", `/api/mother/workspaces/${workspaceId}/block`);
      if (!res.ok) throw new Error("Failed to block workspace");
      return res.json();
    },
    onSuccess: () => { toast({ title: "Workspace blocked" }); refetchFlagged(); },
    onError: () => toast({ title: "Error", description: "Could not block workspace", variant: "destructive" }),
  });

  const approveWorkspaceMutation = useMutation({
    mutationFn: async (workspaceId: number) => {
      const res = await apiRequest("PATCH", `/api/mother/workspaces/${workspaceId}/approve`);
      if (!res.ok) throw new Error("Failed to approve workspace");
      return res.json();
    },
    onSuccess: () => { toast({ title: "Workspace approved and unflagged" }); refetchFlagged(); },
    onError: () => toast({ title: "Error", description: "Could not approve workspace", variant: "destructive" }),
  });

  const createTeamMutation = useMutation({
    mutationFn: async (data: { name: string; description: string; capacityMax: number }) => {
      const response = await apiRequest("POST", "/api/mother/teams", data);
      if (!response.ok) throw new Error("Failed to create team");
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/mother/teams"] });
      setShowAddTeamModal(false);
      setNewTeamName("");
      setNewTeamDescription("");
      setNewTeamCapacity("10");
      toast({ title: "Team created", description: "New team has been added successfully." });
    },
    onError: () => {
      toast({ title: "Error", description: "Failed to create team.", variant: "destructive" });
    }
  });

  const assignQueueItemMutation = useMutation({
    mutationFn: async (data: { itemId: number; assignedUserId: number }) => {
      const response = await apiRequest("POST", `/api/mother/queues/${data.itemId}/assign`, { assignToUserId: data.assignedUserId });
      if (!response.ok) throw new Error("Failed to assign task");
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/mother/queues"] });
      setShowAssignModal(false);
      setSelectedQueueItem(null);
      setSelectedAgentForAssign("");
      toast({ title: "Task assigned", description: "Work queue item has been assigned." });
    },
    onError: () => {
      toast({ title: "Error", description: "Failed to assign task.", variant: "destructive" });
    }
  });

  const markNotificationReadMutation = useMutation({
    mutationFn: async (notificationId: number) => {
      const response = await apiRequest("POST", `/api/mother/notifications/${notificationId}/read`);
      if (!response.ok) throw new Error("Failed to mark as read");
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/mother/notifications"] });
    }
  });

  const generateApiKeyMutation = useMutation({
    mutationFn: async (data: { name: string; scope: string; expiresInDays?: number }) => {
      const response = await apiRequest("POST", "/api/api-keys/generate", data);
      if (!response.ok) throw new Error("Failed to generate API key");
      return response.json();
    },
    onSuccess: (data) => {
      setGeneratedKey(data.apiKey);
      refetchApiKeys();
      toast({ title: "API Key Generated", description: "Copy the key now - it won't be shown again." });
    },
    onError: () => {
      toast({ title: "Error", description: "Failed to generate API key.", variant: "destructive" });
    }
  });

  const revokeApiKeyMutation = useMutation({
    mutationFn: async (keyId: number) => {
      const response = await apiRequest("POST", `/api/api-keys/${keyId}/revoke`);
      if (!response.ok) throw new Error("Failed to revoke API key");
      return response.json();
    },
    onSuccess: () => {
      refetchApiKeys();
      toast({ title: "API Key Revoked", description: "The API key has been revoked." });
    },
    onError: () => {
      toast({ title: "Error", description: "Failed to revoke API key.", variant: "destructive" });
    }
  });

  const deleteApiKeyMutation = useMutation({
    mutationFn: async (keyId: number) => {
      const response = await apiRequest("DELETE", `/api/api-keys/${keyId}`);
      if (!response.ok) throw new Error("Failed to delete API key");
      return response.json();
    },
    onSuccess: () => {
      refetchApiKeys();
      toast({ title: "API Key Deleted", description: "The API key has been permanently deleted." });
    },
    onError: () => {
      toast({ title: "Error", description: "Failed to delete API key.", variant: "destructive" });
    }
  });

  const createSandboxRunMutation = useMutation({
    mutationFn: async (config: any) => {
      const response = await apiRequest("POST", "/api/sandbox/runs", config);
      if (!response.ok) throw new Error("Failed to create sandbox run");
      return response.json();
    },
    onSuccess: (data) => {
      refetchSandboxRuns();
      setSelectedSandboxRun(data.runId);
      setShowCreateRunModal(false);
      toast({ title: "Run Created", description: `Sandbox run ${data.runId.slice(0, 8)} created.` });
    },
    onError: () => {
      toast({ title: "Error", description: "Failed to create sandbox run.", variant: "destructive" });
    }
  });

  const startSandboxRunMutation = useMutation({
    mutationFn: async (runId: string) => {
      const response = await apiRequest("POST", `/api/sandbox/runs/${runId}/start`);
      if (!response.ok) throw new Error("Failed to start sandbox run");
      return response.json();
    },
    onSuccess: () => {
      refetchSandboxRuns();
      refetchSelectedRun();
      toast({ title: "Run Completed", description: "Sandbox simulation completed successfully." });
    },
    onError: () => {
      toast({ title: "Error", description: "Failed to run sandbox simulation.", variant: "destructive" });
    }
  });

  const deleteSandboxRunMutation = useMutation({
    mutationFn: async (runId: string) => {
      const response = await apiRequest("DELETE", `/api/sandbox/runs/${runId}`);
      if (!response.ok) throw new Error("Failed to delete run");
      return response.json();
    },
    onSuccess: () => {
      refetchSandboxRuns();
      setSelectedSandboxRun(null);
      toast({ title: "Run Deleted", description: "Sandbox run deleted." });
    },
    onError: () => {
      toast({ title: "Error", description: "Failed to delete sandbox run.", variant: "destructive" });
    }
  });

  const handleCreateSandboxRun = () => {
    const irrelevantPct = 100 - parseInt(sandboxStrongPct) - parseInt(sandboxBorderlinePct);
    createSandboxRunMutation.mutate({
      totalApplicants: parseInt(sandboxApplicants),
      timeWindowMinutes: parseInt(sandboxTimeWindow),
      agentCount: parseInt(sandboxAgentCount),
      roleFamilies: [sandboxRoleFamily],
      processingModel: sandboxProcessingModel,
      assignmentPolicy: sandboxAssignmentPolicy,
      suitabilityMix: {
        strong: parseInt(sandboxStrongPct),
        borderline: parseInt(sandboxBorderlinePct),
        irrelevant: irrelevantPct
      },
      duplicatesRate: parseInt(sandboxDuplicateRate),
      invalidDataRate: parseInt(sandboxInvalidRate),
      attachmentsRate: 50
    });
  };

  const handleGenerateKey = () => {
    if (!newKeyName.trim()) {
      toast({ title: "Name required", description: "Please enter a name for the API key.", variant: "destructive" });
      return;
    }
    const expiresInDays = newKeyExpiry === "never" ? undefined : parseInt(newKeyExpiry);
    generateApiKeyMutation.mutate({ name: newKeyName, scope: newKeyScope, expiresInDays });
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    toast({ title: "Copied", description: "API key copied to clipboard." });
  };

  // Export functions
  const handleExportAuditLogs = async () => {
    try {
      const response = await apiRequest("GET", "/api/mother/reports/export?type=audit");
      if (!response.ok) throw new Error("Export failed");
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `audit-logs-${new Date().toISOString().split("T")[0]}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
      toast({ title: "Export complete", description: "Audit logs downloaded successfully." });
    } catch {
      toast({ title: "Export failed", description: "Could not export audit logs.", variant: "destructive" });
    }
  };

  const handleExportMetrics = async () => {
    try {
      const response = await apiRequest("GET", "/api/mother/reports/export?type=metrics");
      if (!response.ok) throw new Error("Export failed");
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `metrics-${new Date().toISOString().split("T")[0]}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
      toast({ title: "Export complete", description: "Metrics report downloaded successfully." });
    } catch {
      toast({ title: "Export failed", description: "Could not export metrics.", variant: "destructive" });
    }
  };

  const handleCreateTeam = () => {
    if (!newTeamName.trim()) {
      toast({ title: "Validation error", description: "Team name is required.", variant: "destructive" });
      return;
    }
    createTeamMutation.mutate({
      name: newTeamName.trim(),
      description: newTeamDescription.trim(),
      capacityMax: parseInt(newTeamCapacity) || 10
    });
  };

  const handleAssignTask = () => {
    if (!selectedQueueItem || !selectedAgentForAssign) {
      toast({ title: "Validation error", description: "Please select an agent.", variant: "destructive" });
      return;
    }
    assignQueueItemMutation.mutate({
      itemId: selectedQueueItem.id,
      assignedUserId: parseInt(selectedAgentForAssign)
    });
  };

  const openAssignModal = (item: WorkQueueItem) => {
    setSelectedQueueItem(item);
    setShowAssignModal(true);
  };

  const getSlaStatusBadge = (status: string) => {
    switch (status) {
      case "on_track":
        return <Badge className="bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400">On Track</Badge>;
      case "at_risk":
        return <Badge className="bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400">At Risk</Badge>;
      case "breached":
        return <Badge className="bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400">Breached</Badge>;
      default:
        return <Badge variant="outline">{status}</Badge>;
    }
  };

  const getModeBadge = (mode: string) => {
    return mode === "RPO" 
      ? <Badge className="bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400">Assessment</Badge>
      : <Badge className="bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-400">Placement</Badge>;
  };

  return (
    <div className="min-h-screen bg-background">
      <div className="sticky top-0 z-50 border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60">
        <div className="flex h-16 items-center px-4 md:px-6">
          <div className="flex items-center gap-2 font-semibold text-lg">
            <LayoutDashboard className="h-6 w-6 text-primary" />
            <span>Mother Dashboard</span>
          </div>
          
          <div className="ml-auto flex items-center gap-2 md:gap-4">
            <div className="relative hidden sm:block">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input 
                placeholder="Search..." 
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-40 md:w-64 pl-9"
                data-testid="input-global-search"
              />
            </div>
            
            <Button 
              variant="ghost" 
              size="icon" 
              className="relative" 
              onClick={() => setShowNotificationsModal(true)}
              data-testid="button-notifications"
            >
              <Bell className="h-5 w-5" />
              {unreadNotifications.length > 0 && (
                <span className="absolute -top-1 -right-1 h-5 w-5 rounded-full bg-red-500 text-white text-xs flex items-center justify-center">
                  {unreadNotifications.length}
                </span>
              )}
            </Button>
          </div>
        </div>
      </div>

      {/* Mobile Navigation */}
      <div className="md:hidden border-b overflow-x-auto">
        <div className="flex px-2 py-2 gap-1 min-w-max">
          {[
            { id: "overview", label: "Overview", icon: LayoutDashboard },
            { id: "agents", label: "Recruiters", icon: Users },
            { id: "clients", label: "Workspaces", icon: Building2 },
            { id: "requisitions", label: "Reqs", icon: FileText },
            { id: "queues", label: "Queues", icon: ListTodo },
            { id: "compliance", label: "Audit", icon: Shield },
            { id: "apikeys", label: "API Keys", icon: Key },
            { id: "comms", label: "Comms", icon: Mail },
            { id: "feedback", label: "Feedback", icon: MessageSquare },
            { id: "flagged", label: "Flagged", icon: Flag },
            { id: "analytics", label: "Analytics", icon: BarChart2 },
            { id: "sandbox", label: "Sandbox", icon: FlaskConical },
            { id: "settings", label: "Settings", icon: Settings }
          ].map(item => (
            <Button
              key={item.id}
              variant={activeTab === item.id ? "secondary" : "ghost"}
              size="sm"
              className="flex-shrink-0"
              onClick={() => setActiveTab(item.id)}
              data-testid={`mobile-nav-${item.id}`}
            >
              <item.icon className="h-4 w-4 mr-1" />
              <span className="text-xs">{item.label}</span>
            </Button>
          ))}
        </div>
      </div>

      <div className="flex">
        <aside className="hidden md:block w-64 border-r min-h-[calc(100vh-4rem)] p-4">
          <nav className="space-y-1">
            {[
              { id: "overview", label: "Overview", icon: LayoutDashboard },
              { id: "agents", label: "Recruiters", icon: Users },
              { id: "clients", label: "Workspaces", icon: Building2 },
              { id: "requisitions", label: "Requisitions", icon: FileText },
              { id: "queues", label: "Work Queues", icon: ListTodo },
              { id: "compliance", label: "Compliance & Audit", icon: Shield },
              { id: "apikeys", label: "API Keys", icon: Key },
              { id: "comms", label: "Comm Health", icon: Mail },
              { id: "feedback", label: "Feedback", icon: MessageSquare },
              { id: "flagged", label: "Flagged Accounts", icon: Flag },
              { id: "analytics", label: "Analytics", icon: BarChart2 },
              { id: "sandbox", label: "Sandbox Testing", icon: FlaskConical },
              { id: "settings", label: "Settings", icon: Settings }
            ].map(item => (
              <Button
                key={item.id}
                variant={activeTab === item.id ? "secondary" : "ghost"}
                className="w-full justify-start"
                onClick={() => setActiveTab(item.id)}
                data-testid={`nav-${item.id}`}
              >
                <item.icon className="mr-2 h-4 w-4" />
                {item.label}
              </Button>
            ))}
          </nav>
        </aside>

        <main className="flex-1 p-4 md:p-6">
          {activeTab === "overview" && (
            <div className="space-y-6">
              <h1 className="text-2xl font-bold">Dashboard Overview</h1>
              
              <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
                <Card data-testid="card-active-reqs">
                  <CardHeader className="flex flex-row items-center justify-between pb-2">
                    <CardTitle className="text-sm font-medium">Active Requisitions</CardTitle>
                    <FileText className="h-4 w-4 text-muted-foreground" />
                  </CardHeader>
                  <CardContent>
                    <div className="text-2xl font-bold">{overview?.totalActiveReqs || 0}</div>
                    <p className="text-xs text-muted-foreground">Total open positions</p>
                  </CardContent>
                </Card>

                <Card data-testid="card-at-risk">
                  <CardHeader className="flex flex-row items-center justify-between pb-2">
                    <CardTitle className="text-sm font-medium">At Risk</CardTitle>
                    <AlertTriangle className="h-4 w-4 text-yellow-500" />
                  </CardHeader>
                  <CardContent>
                    <div className="text-2xl font-bold text-yellow-600">{overview?.recsAtRisk || 0}</div>
                    <p className="text-xs text-muted-foreground">SLA at risk</p>
                  </CardContent>
                </Card>

                <Card data-testid="card-breached">
                  <CardHeader className="flex flex-row items-center justify-between pb-2">
                    <CardTitle className="text-sm font-medium">Breached</CardTitle>
                    <Clock className="h-4 w-4 text-red-500" />
                  </CardHeader>
                  <CardContent>
                    <div className="text-2xl font-bold text-red-600">{overview?.recsBreadched || 0}</div>
                    <p className="text-xs text-muted-foreground">SLA breached</p>
                  </CardContent>
                </Card>

                <Card data-testid="card-sla-attainment">
                  <CardHeader className="flex flex-row items-center justify-between pb-2">
                    <CardTitle className="text-sm font-medium">SLA Attainment</CardTitle>
                    <TrendingUp className="h-4 w-4 text-green-500" />
                  </CardHeader>
                  <CardContent>
                    <div className="text-2xl font-bold text-green-600">{overview?.slaAttainment || 100}%</div>
                    <p className="text-xs text-muted-foreground">On-time delivery</p>
                  </CardContent>
                </Card>
              </div>

              <div className="grid gap-4 md:grid-cols-2">
                <Card>
                  <CardHeader>
                    <CardTitle>Pipeline Bottlenecks</CardTitle>
                    <CardDescription>Candidates stuck at each stage</CardDescription>
                  </CardHeader>
                  <CardContent>
                    {overview?.bottleneckStages && overview.bottleneckStages.length > 0 ? (
                      <div className="space-y-2">
                        {overview.bottleneckStages.map((stage, idx) => (
                          <div key={idx} className="flex items-center justify-between">
                            <span className="text-sm capitalize">{stage.stage.replace(/_/g, ' ')}</span>
                            <Badge variant="outline">{stage.count}</Badge>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-sm text-muted-foreground">No bottlenecks detected</p>
                    )}
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader>
                    <CardTitle>Urgent Tasks</CardTitle>
                    <CardDescription>Work queue items needing attention</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="text-3xl font-bold">{overview?.urgentQueues || 0}</div>
                    <p className="text-sm text-muted-foreground mt-2">Pending tasks in queue</p>
                    <Button 
                      variant="outline" 
                      className="mt-4" 
                      onClick={() => setActiveTab("queues")}
                      data-testid="button-view-queues"
                    >
                      View Work Queue
                    </Button>
                  </CardContent>
                </Card>
              </div>

              {/* New Registrations Panel */}
              <Card data-testid="card-new-registrations">
                <CardHeader className="pb-3">
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-base">New Registrations</CardTitle>
                    {adminNotifs && adminNotifs.filter(n => !n.read).length > 0 && (
                      <Badge variant="secondary" data-testid="badge-unread-registrations">
                        {adminNotifs.filter(n => !n.read).length} unread
                      </Badge>
                    )}
                  </div>
                  <CardDescription>Direct recruiter sign-ups from the public registration page</CardDescription>
                </CardHeader>
                <CardContent>
                  {!adminNotifs || adminNotifs.length === 0 ? (
                    <p className="text-sm text-muted-foreground py-2" data-testid="text-no-registrations">No registrations yet.</p>
                  ) : (
                    <div className="space-y-2">
                      {adminNotifs.map(notif => {
                        const p = notif.payload as any;
                        const relTime = (() => {
                          const diff = Date.now() - new Date(notif.createdAt).getTime();
                          const mins = Math.floor(diff / 60000);
                          if (mins < 60) return `${mins}m ago`;
                          const hrs = Math.floor(mins / 60);
                          if (hrs < 24) return `${hrs}h ago`;
                          return `${Math.floor(hrs / 24)}d ago`;
                        })();
                        return (
                          <div
                            key={notif.id}
                            className={`flex items-start justify-between gap-3 p-3 rounded-lg border text-sm transition-colors ${notif.read ? "bg-muted/30 border-border" : "bg-blue-50 dark:bg-blue-950/30 border-blue-200 dark:border-blue-800"}`}
                            data-testid={`registration-notif-${notif.id}`}
                          >
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2 flex-wrap">
                                {!notif.read && <span className="w-2 h-2 rounded-full bg-blue-500 flex-shrink-0" />}
                                <span className="font-medium truncate">{p.agencyName || "Unknown Agency"}</span>
                                <span className="text-muted-foreground text-xs">{p.email}</span>
                              </div>
                              {p.currentTools && (
                                <p className="text-xs text-muted-foreground mt-0.5">Current tools: {p.currentTools}</p>
                              )}
                              <p className="text-xs text-muted-foreground mt-0.5">{relTime}</p>
                            </div>
                            {!notif.read && (
                              <Button
                                size="sm"
                                variant="ghost"
                                className="h-7 text-xs flex-shrink-0"
                                onClick={() => markAdminNotifRead.mutate(notif.id)}
                                data-testid={`button-mark-read-${notif.id}`}
                              >
                                Mark read
                              </Button>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          )}

          {activeTab === "agents" && (
            <div className="space-y-6">
              <div className="flex items-center justify-between">
                <h1 className="text-2xl font-bold">Recruiters</h1>
                <Button onClick={() => setShowAddTeamModal(true)} data-testid="button-add-team">
                  <Plus className="mr-2 h-4 w-4" />
                  Add Team
                </Button>
              </div>

              <div className="grid gap-4 md:grid-cols-3 mb-6">
                {teams?.map(team => (
                  <Card key={team.id} data-testid={`card-team-${team.id}`}>
                    <CardHeader>
                      <CardTitle className="text-lg">{team.name}</CardTitle>
                      <CardDescription>{team.description || 'No description'}</CardDescription>
                    </CardHeader>
                    <CardContent>
                      <div className="flex items-center justify-between text-sm">
                        <span>Capacity</span>
                        <span>{team.capacityMax} max</span>
                      </div>
                    </CardContent>
                  </Card>
                ))}
                {(!teams || teams.length === 0) && (
                  <Card className="col-span-3">
                    <CardContent className="py-8 text-center text-muted-foreground">
                      No teams configured yet. Create a team to organize agents.
                    </CardContent>
                  </Card>
                )}
              </div>

              <Card>
                <CardHeader>
                  <CardTitle>Agent Capacity & Performance</CardTitle>
                </CardHeader>
                <CardContent className="overflow-x-auto">
                  <Table className="min-w-[700px]">
                    <TableHeader>
                      <TableRow>
                        <TableHead>Agent</TableHead>
                        <TableHead>Team</TableHead>
                        <TableHead>Active Reqs</TableHead>
                        <TableHead>Open Tasks</TableHead>
                        <TableHead>Completed (Month)</TableHead>
                        <TableHead>SLA Breaches</TableHead>
                        <TableHead>Utilization</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {agents?.map(agent => (
                        <TableRow key={agent.userId} data-testid={`row-agent-${agent.userId}`}>
                          <TableCell>
                            <div>
                              <div className="font-medium">{agent.fullName}</div>
                              <div className="text-sm text-muted-foreground">{agent.email}</div>
                            </div>
                          </TableCell>
                          <TableCell>{agent.teamName || '-'}</TableCell>
                          <TableCell>{agent.activeReqCount}</TableCell>
                          <TableCell>{agent.openTasks}</TableCell>
                          <TableCell>{agent.completedThisMonth}</TableCell>
                          <TableCell>
                            {agent.slaBreaches > 0 ? (
                              <Badge variant="destructive">{agent.slaBreaches}</Badge>
                            ) : (
                              <Badge variant="outline">0</Badge>
                            )}
                          </TableCell>
                          <TableCell>
                            <div className="flex items-center gap-2">
                              <div className="w-16 h-2 bg-muted rounded-full overflow-hidden">
                                <div 
                                  className={`h-full ${agent.utilization > 80 ? 'bg-red-500' : agent.utilization > 60 ? 'bg-yellow-500' : 'bg-green-500'}`}
                                  style={{ width: `${agent.utilization}%` }}
                                />
                              </div>
                              <span className="text-sm">{agent.utilization}%</span>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                      {(!agents || agents.length === 0) && (
                        <TableRow>
                          <TableCell colSpan={7} className="text-center text-muted-foreground py-8">
                            No agents found
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </CardContent>
              </Card>
            </div>
          )}

          {activeTab === "clients" && (
            <div className="space-y-6">
              <div className="flex items-center justify-between">
                <h1 className="text-2xl font-bold">Workspaces</h1>
              </div>

              <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                {clients?.map(client => (
                  <Card key={client.id} data-testid={`card-client-${client.id}`}>
                    <CardHeader>
                      <div className="flex items-center justify-between">
                        <CardTitle className="text-lg">{client.companyName}</CardTitle>
                        <Badge variant="outline">{client.tier}</Badge>
                      </div>
                      <CardDescription>{client.email}</CardDescription>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-2 text-sm">
                        <div className="flex justify-between">
                          <span>Active Requisitions</span>
                          <span className="font-medium">{client.activeReqCount}</span>
                        </div>
                        <div className="flex justify-between">
                          <span>SLA Config</span>
                          <span className="font-medium">{client.slaConfig?.name || 'Default'}</span>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
                {(!clients || clients.length === 0) && (
                  <Card className="col-span-3">
                    <CardContent className="py-8 text-center text-muted-foreground">
                      No clients found
                    </CardContent>
                  </Card>
                )}
              </div>
            </div>
          )}

          {activeTab === "requisitions" && (
            <div className="space-y-6">
              <div>
                <div className="flex items-center justify-between">
                  <h1 className="text-2xl font-bold">Requisitions</h1>
                  <div className="flex gap-2">
                  <Select value={modeFilter} onValueChange={setModeFilter}>
                    <SelectTrigger className="w-32" data-testid="select-mode-filter">
                      <SelectValue placeholder="Mode" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Modes</SelectItem>
                      <SelectItem value="RPO">Assessment</SelectItem>
                      <SelectItem value="PLACEMENT">Placement</SelectItem>
                    </SelectContent>
                  </Select>
                  <Select value={statusFilter} onValueChange={setStatusFilter}>
                    <SelectTrigger className="w-32" data-testid="select-status-filter">
                      <SelectValue placeholder="Status" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Status</SelectItem>
                      <SelectItem value="active">Active</SelectItem>
                      <SelectItem value="filled">Filled</SelectItem>
                      <SelectItem value="closed">Closed</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                </div>
                <p className="text-sm text-muted-foreground mt-2">This view shows all active workspaces and roles across the platform. Test data can be cleared from Settings.</p>
              </div>

              <Card>
                <CardContent className="p-0 overflow-x-auto">
                  <Table className="min-w-[700px]">
                    <TableHeader>
                      <TableRow>
                        <TableHead>Title</TableHead>
                        <TableHead>Client</TableHead>
                        <TableHead>Mode</TableHead>
                        <TableHead>Owner</TableHead>
                        <TableHead>Candidates</TableHead>
                        <TableHead>SLA Status</TableHead>
                        <TableHead>Created</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {requisitionsData?.requisitions?.map(req => (
                        <TableRow key={req.id} data-testid={`row-req-${req.id}`}>
                          <TableCell className="font-medium">{req.title}</TableCell>
                          <TableCell>{req.clientName}</TableCell>
                          <TableCell>{getModeBadge(req.mode)}</TableCell>
                          <TableCell>
                            <div>
                              <div className="text-sm">{req.ownerAgentName || '-'}</div>
                              {req.ownerTeamName && (
                                <div className="text-xs text-muted-foreground">{req.ownerTeamName}</div>
                              )}
                            </div>
                          </TableCell>
                          <TableCell>{req.candidateCount}</TableCell>
                          <TableCell>{getSlaStatusBadge(req.slaStatus)}</TableCell>
                          <TableCell className="text-sm text-muted-foreground">
                            {new Date(req.createdAt).toLocaleDateString()}
                          </TableCell>
                        </TableRow>
                      ))}
                      {(!requisitionsData?.requisitions || requisitionsData.requisitions.length === 0) && (
                        <TableRow>
                          <TableCell colSpan={7} className="text-center text-muted-foreground py-8">
                            No requisitions found
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </CardContent>
              </Card>
            </div>
          )}

          {activeTab === "queues" && (
            <div className="space-y-6">
              <div className="flex items-center justify-between">
                <h1 className="text-2xl font-bold">Work Queues</h1>
              </div>

              <Card>
                <CardContent className="p-0 overflow-x-auto">
                  <Table className="min-w-[600px]">
                    <TableHeader>
                      <TableRow>
                        <TableHead>Priority</TableHead>
                        <TableHead>Task</TableHead>
                        <TableHead>Type</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead>Due</TableHead>
                        <TableHead>Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {workQueueData?.items?.map(item => (
                        <TableRow key={item.id} data-testid={`row-queue-${item.id}`}>
                          <TableCell>
                            <Badge variant={item.priority <= 3 ? "destructive" : item.priority <= 5 ? "secondary" : "outline"}>
                              P{item.priority}
                            </Badge>
                          </TableCell>
                          <TableCell>
                            <div className="font-medium">{item.title}</div>
                            {item.description && (
                              <div className="text-sm text-muted-foreground truncate max-w-xs">{item.description}</div>
                            )}
                          </TableCell>
                          <TableCell className="capitalize">{item.taskType.replace(/_/g, ' ')}</TableCell>
                          <TableCell>
                            <Badge variant="outline" className="capitalize">{item.status}</Badge>
                          </TableCell>
                          <TableCell className="text-sm">
                            {item.dueAt ? new Date(item.dueAt).toLocaleDateString() : '-'}
                          </TableCell>
                          <TableCell>
                            <Button size="sm" variant="outline" onClick={() => openAssignModal(item)} data-testid={`button-assign-${item.id}`}>
                              Assign
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                      {(!workQueueData?.items || workQueueData.items.length === 0) && (
                        <TableRow>
                          <TableCell colSpan={6} className="text-center text-muted-foreground py-8">
                            No items in queue
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </CardContent>
              </Card>
            </div>
          )}

          {activeTab === "compliance" && (
            <div className="space-y-6">
              <div className="flex items-center justify-between">
                <h1 className="text-2xl font-bold">Compliance & Audit</h1>
                <Button variant="outline" onClick={handleExportAuditLogs} data-testid="button-export-audit">
                  <Download className="mr-2 h-4 w-4" />
                  Export Logs
                </Button>
              </div>

              <Card>
                <CardHeader>
                  <CardTitle>Activity Log</CardTitle>
                  <CardDescription>All system actions with full audit trail</CardDescription>
                </CardHeader>
                <CardContent className="p-0 overflow-x-auto">
                  <Table className="min-w-[600px]">
                    <TableHeader>
                      <TableRow>
                        <TableHead>Timestamp</TableHead>
                        <TableHead>Actor</TableHead>
                        <TableHead>Action</TableHead>
                        <TableHead>Entity</TableHead>
                        <TableHead>Details</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {auditData?.logs?.map((log: any) => (
                        <TableRow key={log.id} data-testid={`row-audit-${log.id}`}>
                          <TableCell className="text-sm">
                            {new Date(log.createdAt).toLocaleString()}
                          </TableCell>
                          <TableCell>{log.agentEmail || 'System'}</TableCell>
                          <TableCell>
                            <Badge variant="outline" className="capitalize">
                              {log.eventType.replace(/_/g, ' ')}
                            </Badge>
                          </TableCell>
                          <TableCell>
                            {log.entityType} #{log.entityId}
                          </TableCell>
                          <TableCell className="text-sm text-muted-foreground truncate max-w-xs">
                            {log.metadata ? JSON.stringify(log.metadata) : '-'}
                          </TableCell>
                        </TableRow>
                      ))}
                      {(!auditData?.logs || auditData.logs.length === 0) && (
                        <TableRow>
                          <TableCell colSpan={5} className="text-center text-muted-foreground py-8">
                            No audit logs found
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </CardContent>
              </Card>
            </div>
          )}

          {activeTab === "apikeys" && (
            <div className="space-y-6">
              <div className="flex items-center justify-between">
                <div>
                  <h1 className="text-2xl font-bold">API Keys</h1>
                  <p className="text-muted-foreground">Manage API keys for external integrations</p>
                </div>
                <Button onClick={() => { setShowGenerateKeyModal(true); setGeneratedKey(null); setNewKeyName(""); }} data-testid="btn-generate-key">
                  <Plus className="h-4 w-4 mr-2" />
                  Generate New Key
                </Button>
              </div>

              {generatedKey && (
                <Card className="border-green-500 bg-green-50 dark:bg-green-900/20">
                  <CardContent className="pt-6">
                    <div className="flex items-center gap-2 mb-2">
                      <CheckCircle2 className="h-5 w-5 text-green-600" />
                      <span className="font-semibold text-green-700 dark:text-green-400">API Key Generated Successfully</span>
                    </div>
                    <p className="text-sm text-muted-foreground mb-3">Copy this key now. You won't be able to see it again.</p>
                    <div className="flex items-center gap-2 bg-background border rounded-md p-3">
                      <code className="flex-1 font-mono text-sm break-all">
                        {showKey ? generatedKey : generatedKey.replace(/./g, "•")}
                      </code>
                      <Button variant="ghost" size="sm" onClick={() => setShowKey(!showKey)} data-testid="btn-toggle-key-visibility">
                        {showKey ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                      </Button>
                      <Button variant="ghost" size="sm" onClick={() => copyToClipboard(generatedKey)} data-testid="btn-copy-key">
                        <Copy className="h-4 w-4" />
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              )}

              <Card>
                <CardHeader>
                  <CardTitle>Active API Keys</CardTitle>
                  <CardDescription>Keys used for dossier sync and external integrations</CardDescription>
                </CardHeader>
                <CardContent>
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Name</TableHead>
                        <TableHead>Key</TableHead>
                        <TableHead>Scope</TableHead>
                        <TableHead>Created By</TableHead>
                        <TableHead>Created</TableHead>
                        <TableHead>Expires</TableHead>
                        <TableHead>Last Used</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead className="text-right">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {apiKeys && apiKeys.length > 0 ? apiKeys.map(key => (
                        <TableRow key={key.id} className={key.isRevoked ? "opacity-50" : ""}>
                          <TableCell className="font-medium">{key.name}</TableCell>
                          <TableCell>
                            <code className="text-xs bg-muted px-2 py-1 rounded">{key.keyPrefix}...****</code>
                          </TableCell>
                          <TableCell>
                            <Badge variant="outline">{key.scope}</Badge>
                          </TableCell>
                          <TableCell className="text-sm text-muted-foreground">{key.creatorName}</TableCell>
                          <TableCell className="text-sm text-muted-foreground">
                            {new Date(key.createdAt).toLocaleDateString()}
                          </TableCell>
                          <TableCell className="text-sm text-muted-foreground">
                            {key.expiresAt ? new Date(key.expiresAt).toLocaleDateString() : "Never"}
                          </TableCell>
                          <TableCell className="text-sm text-muted-foreground">
                            {key.lastUsedAt ? new Date(key.lastUsedAt).toLocaleDateString() : "Never"}
                          </TableCell>
                          <TableCell>
                            {key.isRevoked ? (
                              <Badge variant="destructive">Revoked</Badge>
                            ) : key.expiresAt && new Date(key.expiresAt) < new Date() ? (
                              <Badge variant="secondary">Expired</Badge>
                            ) : (
                              <Badge variant="default">Active</Badge>
                            )}
                          </TableCell>
                          <TableCell className="text-right">
                            <div className="flex gap-1 justify-end">
                              {!key.isRevoked && (
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => revokeApiKeyMutation.mutate(key.id)}
                                  disabled={revokeApiKeyMutation.isPending}
                                  data-testid={`btn-revoke-key-${key.id}`}
                                >
                                  <AlertTriangle className="h-4 w-4" />
                                </Button>
                              )}
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => deleteApiKeyMutation.mutate(key.id)}
                                disabled={deleteApiKeyMutation.isPending}
                                className="text-destructive hover:text-destructive"
                                data-testid={`btn-delete-key-${key.id}`}
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      )) : (
                        <TableRow>
                          <TableCell colSpan={9} className="text-center text-muted-foreground py-8">
                            No API keys generated yet. Click "Generate New Key" to create one.
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle>Usage Instructions</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <p className="text-sm text-muted-foreground">
                    Use the generated API key to authenticate requests to the dossier sync endpoints:
                  </p>
                  <div className="bg-muted rounded-md p-4">
                    <code className="text-sm">
                      curl -X POST https://your-domain/api/dossier-sync/sync/123 \<br />
                      &nbsp;&nbsp;-H "Authorization: Bearer YOUR_API_KEY" \<br />
                      &nbsp;&nbsp;-H "Content-Type: application/json"
                    </code>
                  </div>
                  <p className="text-sm text-muted-foreground">
                    Available endpoints:
                  </p>
                  <ul className="text-sm text-muted-foreground list-disc list-inside space-y-1">
                    <li><code>POST /api/dossier-sync/sync/:candidateId</code> - Sync single candidate</li>
                    <li><code>POST /api/dossier-sync/sync-bulk</code> - Sync multiple candidates</li>
                    <li><code>GET /api/dossier-sync/candidate/:candidateId/export</code> - Export candidate data</li>
                  </ul>
                </CardContent>
              </Card>
            </div>
          )}

          {activeTab === "analytics" && <MotherAnalytics />}

          {activeTab === "sandbox" && (
            <div className="space-y-6">
              <div className="bg-orange-500 text-white px-4 py-3 rounded-lg flex items-center gap-3 mb-4">
                <FlaskConical className="h-6 w-6" />
                <div>
                  <div className="font-bold text-lg">SANDBOX ONLY</div>
                  <div className="text-sm opacity-90">All data is synthetic. This environment is isolated from production.</div>
                </div>
              </div>

              <div className="flex items-center justify-between">
                <h1 className="text-2xl font-bold">Sandbox Testing</h1>
                <Button onClick={() => setShowCreateRunModal(true)} disabled={!sandboxStatus?.sandboxMode} data-testid="btn-create-sandbox-run">
                  <Plus className="mr-2 h-4 w-4" />
                  New Test Run
                </Button>
              </div>

              <Card className="mb-6 bg-slate-50 border-slate-200">
                <CardHeader>
                  <CardTitle className="text-lg flex items-center gap-2">
                    <FlaskConical className="h-5 w-5 text-orange-500" />
                    What Does the Sandbox Test?
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <p className="text-sm text-slate-700">
                    The Sandbox simulates how VEXIS handles <strong>bulk candidate processing</strong> under various load conditions. 
                    It uses <strong>synthetic (fake) data</strong> to test system efficiency without touching real candidates.
                  </p>
                  
                  <div className="grid gap-4 md:grid-cols-2">
                    <div className="space-y-2">
                      <h4 className="font-semibold text-sm">What's Being Measured:</h4>
                      <ul className="text-sm text-slate-600 space-y-1 list-disc list-inside">
                        <li><strong>Throughput</strong> - Candidates processed per hour</li>
                        <li><strong>Queue Wait</strong> - Time candidates wait before assignment</li>
                        <li><strong>End-to-End Time</strong> - Total processing time per candidate</li>
                        <li><strong>SLA Compliance</strong> - % completed within 24h/48h targets</li>
                        <li><strong>Agent Utilization</strong> - How busy agents are during the run</li>
                        <li><strong>Bucket Distribution</strong> - A/B/C candidate quality breakdown</li>
                        <li><strong>AI Interview Scores</strong> - Simulated interview performance (0-100)</li>
                        <li><strong>Job Match Scores</strong> - Skill/experience alignment scoring</li>
                        <li><strong>Resume Verification</strong> - AI-detection, timeline checks, risk flags</li>
                      </ul>
                    </div>
                    
                    <div className="space-y-2">
                      <h4 className="font-semibold text-sm">How to Use:</h4>
                      <ol className="text-sm text-slate-600 space-y-1 list-decimal list-inside">
                        <li>Click <strong>New Test Run</strong> to configure a simulation</li>
                        <li>Set applicant count (1-10,000), agent count, time window</li>
                        <li>Choose role family, suitability mix, and assignment policy</li>
                        <li>Click <strong>Start Run</strong> to execute the simulation</li>
                        <li>Review metrics and export results as CSV/JSON</li>
                      </ol>
                    </div>
                  </div>
                  
                  <div className="border-t pt-3 mt-3 space-y-2">
                    <h4 className="font-semibold text-sm text-slate-700">Sandbox vs. Production</h4>
                    <div className="grid gap-2 md:grid-cols-2 text-xs">
                      <div className="bg-amber-50 border border-amber-200 rounded p-2">
                        <span className="font-semibold text-amber-700">Sandbox Mode:</span>
                        <ul className="text-slate-600 mt-1 space-y-0.5">
                          <li>• Synthetic data with realistic distributions</li>
                          <li>• Instant simulated AI scores (no API calls)</li>
                          <li>• Isolated SQLite database</li>
                          <li>• Safe for capacity planning tests</li>
                        </ul>
                      </div>
                      <div className="bg-green-50 border border-green-200 rounded p-2">
                        <span className="font-semibold text-green-700">Production Mode:</span>
                        <ul className="text-slate-600 mt-1 space-y-0.5">
                          <li>• Real candidate interviews</li>
                          <li>• Live OpenAI API scoring</li>
                          <li>• PostgreSQL production database</li>
                          <li>• Actual client workloads</li>
                        </ul>
                      </div>
                    </div>
                    <p className="text-xs text-slate-500">
                      Efficiency metrics (throughput, queue times, SLA compliance) from sandbox runs are directly applicable to real-world capacity planning.
                    </p>
                  </div>
                </CardContent>
              </Card>

              {!sandboxStatus?.sandboxMode ? (
                <Card>
                  <CardContent className="py-12 text-center">
                    <AlertTriangle className="h-12 w-12 mx-auto text-yellow-500 mb-4" />
                    <h3 className="text-lg font-semibold mb-2">Sandbox Mode Not Enabled</h3>
                    <p className="text-muted-foreground mb-4">
                      Set <code className="bg-muted px-2 py-1 rounded">SANDBOX_MODE=true</code> environment variable to enable sandbox testing.
                    </p>
                    <p className="text-sm text-muted-foreground">
                      This ensures complete isolation from production data and systems.
                    </p>
                  </CardContent>
                </Card>
              ) : (
                <div className="grid gap-6 md:grid-cols-3">
                  <div className="md:col-span-1">
                    <Card>
                      <CardHeader>
                        <CardTitle>Test Runs</CardTitle>
                        <CardDescription>Previous simulation runs</CardDescription>
                      </CardHeader>
                      <CardContent className="space-y-2 max-h-[400px] overflow-y-auto">
                        {sandboxRuns?.runs?.length ? sandboxRuns.runs.map((run: any) => (
                          <div
                            key={run.runId}
                            className={`p-3 rounded-lg border cursor-pointer transition-colors ${selectedSandboxRun === run.runId ? 'border-primary bg-primary/5' : 'hover:bg-muted/50'}`}
                            onClick={() => setSelectedSandboxRun(run.runId)}
                            data-testid={`sandbox-run-${run.runId.slice(0, 8)}`}
                          >
                            <div className="flex items-center justify-between">
                              <span className="font-mono text-sm">{run.runId.slice(0, 8)}</span>
                              <Badge variant={run.status === 'completed' ? 'default' : run.status === 'running' ? 'secondary' : 'outline'}>
                                {run.status}
                              </Badge>
                            </div>
                            <div className="text-xs text-muted-foreground mt-1">
                              {run.config?.totalApplicants || 0} applicants
                            </div>
                          </div>
                        )) : (
                          <p className="text-sm text-muted-foreground text-center py-4">No runs yet</p>
                        )}
                      </CardContent>
                    </Card>
                  </div>

                  <div className="md:col-span-2">
                    {selectedRunData?.run ? (
                      <div className="space-y-4">
                        <Card>
                          <CardHeader>
                            <div className="flex items-center justify-between">
                              <div>
                                <CardTitle>Run: {selectedRunData.run.runId.slice(0, 8)}</CardTitle>
                                <CardDescription>
                                  {selectedRunData.run.config?.totalApplicants} applicants, {selectedRunData.run.config?.agentCount} agents
                                </CardDescription>
                              </div>
                              <div className="flex gap-2">
                                {selectedRunData.run.status === 'created' && (
                                  <Button
                                    onClick={() => startSandboxRunMutation.mutate(selectedRunData.run.runId)}
                                    disabled={startSandboxRunMutation.isPending}
                                    data-testid="btn-start-sandbox-run"
                                  >
                                    <Play className="mr-2 h-4 w-4" />
                                    {startSandboxRunMutation.isPending ? 'Running...' : 'Start Run'}
                                  </Button>
                                )}
                                <Button
                                  variant="outline"
                                  onClick={() => window.open(`/api/sandbox/runs/${selectedRunData.run.runId}/export?format=csv`, '_blank')}
                                  data-testid="btn-export-sandbox"
                                >
                                  <Download className="mr-2 h-4 w-4" />
                                  Export
                                </Button>
                                <Button
                                  variant="ghost"
                                  className="text-destructive"
                                  onClick={() => deleteSandboxRunMutation.mutate(selectedRunData.run.runId)}
                                  data-testid="btn-delete-sandbox-run"
                                >
                                  <Trash2 className="h-4 w-4" />
                                </Button>
                              </div>
                            </div>
                          </CardHeader>
                          {selectedRunData.run.status === 'created' && (
                            <CardContent className="bg-amber-50 border-t border-amber-200">
                              <div className="flex items-center gap-3">
                                <AlertTriangle className="h-5 w-5 text-amber-600" />
                                <div>
                                  <p className="font-medium text-amber-800">Run Not Started</p>
                                  <p className="text-sm text-amber-700">Click the "Start Run" button above to execute the simulation and generate metrics.</p>
                                </div>
                              </div>
                            </CardContent>
                          )}
                        </Card>

                        {selectedRunData.metrics && (
                          <>
                            <div className="grid gap-4 md:grid-cols-4">
                              <Card>
                                <CardContent className="pt-4">
                                  <div className="text-2xl font-bold">{selectedRunData.metrics.throughputPerHour}</div>
                                  <div className="text-sm text-muted-foreground">Throughput/hr</div>
                                </CardContent>
                              </Card>
                              <Card>
                                <CardContent className="pt-4">
                                  <div className="text-2xl font-bold">{Math.round(selectedRunData.metrics.medianEndToEndMs / 1000)}s</div>
                                  <div className="text-sm text-muted-foreground">Median E2E</div>
                                </CardContent>
                              </Card>
                              <Card>
                                <CardContent className="pt-4">
                                  <div className="text-2xl font-bold">{Math.round(selectedRunData.metrics.p95EndToEndMs / 1000)}s</div>
                                  <div className="text-sm text-muted-foreground">P95 E2E</div>
                                </CardContent>
                              </Card>
                              <Card>
                                <CardContent className="pt-4">
                                  <div className="text-2xl font-bold">{selectedRunData.metrics.agentUtilization}%</div>
                                  <div className="text-sm text-muted-foreground">Agent Util</div>
                                </CardContent>
                              </Card>
                            </div>

                            <div className="grid gap-4 md:grid-cols-2">
                              <Card>
                                <CardHeader>
                                  <CardTitle className="text-base">SLA Compliance</CardTitle>
                                </CardHeader>
                                <CardContent className="space-y-2">
                                  <div className="flex justify-between">
                                    <span>Within 24h</span>
                                    <span className="font-bold text-green-600">{selectedRunData.metrics.slaCompliance.within24h}%</span>
                                  </div>
                                  <div className="flex justify-between">
                                    <span>Within 48h</span>
                                    <span className="font-bold text-green-600">{selectedRunData.metrics.slaCompliance.within48h}%</span>
                                  </div>
                                </CardContent>
                              </Card>
                              <Card>
                                <CardHeader>
                                  <CardTitle className="text-base">Bucket Distribution</CardTitle>
                                </CardHeader>
                                <CardContent className="space-y-2">
                                  <div className="flex justify-between">
                                    <span>Bucket A (Strong)</span>
                                    <span className="font-bold">{selectedRunData.metrics.bucketDistribution.A}</span>
                                  </div>
                                  <div className="flex justify-between">
                                    <span>Bucket B (Borderline)</span>
                                    <span className="font-bold">{selectedRunData.metrics.bucketDistribution.B}</span>
                                  </div>
                                  <div className="flex justify-between">
                                    <span>Bucket C (Irrelevant)</span>
                                    <span className="font-bold">{selectedRunData.metrics.bucketDistribution.C}</span>
                                  </div>
                                </CardContent>
                              </Card>
                            </div>

                            <Card>
                              <CardHeader>
                                <CardTitle className="text-base">Bottleneck Analysis</CardTitle>
                              </CardHeader>
                              <CardContent>
                                <div className="flex gap-4">
                                  <div className="flex-1 p-3 rounded bg-muted/50">
                                    <div className="text-sm text-muted-foreground">Queueing</div>
                                    <div className="font-bold">{Math.round(selectedRunData.metrics.bottleneckAnalysis.queueingDelayMs)}ms</div>
                                  </div>
                                  <div className="flex-1 p-3 rounded bg-muted/50">
                                    <div className="text-sm text-muted-foreground">Assignment</div>
                                    <div className="font-bold">{Math.round(selectedRunData.metrics.bottleneckAnalysis.assignmentDelayMs)}ms</div>
                                  </div>
                                  <div className="flex-1 p-3 rounded bg-muted/50">
                                    <div className="text-sm text-muted-foreground">Processing</div>
                                    <div className="font-bold">{Math.round(selectedRunData.metrics.bottleneckAnalysis.processingDelayMs)}ms</div>
                                  </div>
                                </div>
                                <div className="mt-3 text-sm">
                                  <Badge variant="secondary">Bottleneck: {selectedRunData.metrics.bottleneckAnalysis.bottleneck}</Badge>
                                </div>
                              </CardContent>
                            </Card>

                            {selectedRunData.metrics.aiMetrics && (
                              <>
                                <Card className="border-indigo-300 bg-gradient-to-br from-indigo-50 via-purple-50 to-blue-50">
                                  <CardHeader className="border-b border-indigo-100 bg-indigo-600 text-white rounded-t-lg">
                                    <CardTitle className="text-base flex items-center gap-2">
                                      <Activity className="h-4 w-4" />
                                      AI Simulation Metrics
                                    </CardTitle>
                                    <CardDescription className="text-indigo-100">Simulated AI scoring and verification results</CardDescription>
                                  </CardHeader>
                                  <CardContent className="pt-4">
                                    <div className="grid gap-4 md:grid-cols-4 mb-4">
                                      <div className="p-4 rounded-lg bg-white shadow-sm border-l-4 border-blue-500">
                                        <div className="text-3xl font-bold text-blue-600">{selectedRunData.metrics.aiMetrics.avgInterviewScore}</div>
                                        <div className="text-sm font-medium text-slate-600">Avg Interview Score</div>
                                      </div>
                                      <div className="p-4 rounded-lg bg-white shadow-sm border-l-4 border-green-500">
                                        <div className="text-3xl font-bold text-green-600">{selectedRunData.metrics.aiMetrics.avgJobMatchScore}</div>
                                        <div className="text-sm font-medium text-slate-600">Avg Job Match</div>
                                      </div>
                                      <div className="p-4 rounded-lg bg-white shadow-sm border-l-4 border-slate-500">
                                        <div className="text-3xl font-bold text-slate-700">{selectedRunData.metrics.aiMetrics.avgAiProcessingTimeMs}ms</div>
                                        <div className="text-sm font-medium text-slate-600">Avg AI Processing</div>
                                      </div>
                                      <div className="p-4 rounded-lg bg-white shadow-sm border-l-4 border-orange-500">
                                        <div className="text-3xl font-bold text-orange-600">{selectedRunData.metrics.aiMetrics.aiWrittenDetectionRate}%</div>
                                        <div className="text-sm font-medium text-slate-600">AI-Written Detected</div>
                                      </div>
                                    </div>
                                    
                                    <div className="grid gap-4 md:grid-cols-2">
                                      <div className="bg-white rounded-lg p-4 shadow-sm">
                                        <h4 className="font-semibold text-sm mb-3 text-indigo-700 border-b pb-2">Score Distribution</h4>
                                        <div className="space-y-2">
                                          <div className="flex justify-between items-center text-sm">
                                            <span className="flex items-center gap-2"><span className="w-3 h-3 rounded-full bg-green-500"></span>Excellent (80+)</span>
                                            <span className="font-bold text-lg">{selectedRunData.metrics.aiMetrics.scoreDistribution.excellent}</span>
                                          </div>
                                          <div className="flex justify-between items-center text-sm">
                                            <span className="flex items-center gap-2"><span className="w-3 h-3 rounded-full bg-blue-500"></span>Good (60-79)</span>
                                            <span className="font-bold text-lg">{selectedRunData.metrics.aiMetrics.scoreDistribution.good}</span>
                                          </div>
                                          <div className="flex justify-between items-center text-sm">
                                            <span className="flex items-center gap-2"><span className="w-3 h-3 rounded-full bg-yellow-500"></span>Average (40-59)</span>
                                            <span className="font-bold text-lg">{selectedRunData.metrics.aiMetrics.scoreDistribution.average}</span>
                                          </div>
                                          <div className="flex justify-between items-center text-sm">
                                            <span className="flex items-center gap-2"><span className="w-3 h-3 rounded-full bg-red-500"></span>Poor (&lt;40)</span>
                                            <span className="font-bold text-lg">{selectedRunData.metrics.aiMetrics.scoreDistribution.poor}</span>
                                          </div>
                                        </div>
                                      </div>
                                      <div className="bg-white rounded-lg p-4 shadow-sm">
                                        <h4 className="font-semibold text-sm mb-3 text-indigo-700 border-b pb-2">Resume Risk Distribution</h4>
                                        <div className="space-y-2">
                                          <div className="flex justify-between items-center text-sm">
                                            <span className="flex items-center gap-2"><span className="w-3 h-3 rounded-full bg-green-500"></span>Low Risk</span>
                                            <span className="font-bold text-lg text-green-600">{selectedRunData.metrics.aiMetrics.resumeRiskDistribution.low}</span>
                                          </div>
                                          <div className="flex justify-between items-center text-sm">
                                            <span className="flex items-center gap-2"><span className="w-3 h-3 rounded-full bg-yellow-500"></span>Medium Risk</span>
                                            <span className="font-bold text-lg text-yellow-600">{selectedRunData.metrics.aiMetrics.resumeRiskDistribution.medium}</span>
                                          </div>
                                          <div className="flex justify-between items-center text-sm">
                                            <span className="flex items-center gap-2"><span className="w-3 h-3 rounded-full bg-red-500"></span>High Risk</span>
                                            <span className="font-bold text-lg text-red-600">{selectedRunData.metrics.aiMetrics.resumeRiskDistribution.high}</span>
                                          </div>
                                        </div>
                                      </div>
                                    </div>
                                  </CardContent>
                                </Card>
                              </>
                            )}

                            <Card>
                              <CardHeader>
                                <CardTitle className="text-base">Full Data Breakdown</CardTitle>
                                <CardDescription>Complete metrics summary for this test run</CardDescription>
                              </CardHeader>
                              <CardContent>
                                <div className="grid gap-6 md:grid-cols-3 mb-6">
                                  <div className="space-y-3">
                                    <h4 className="font-semibold text-sm border-b pb-1">Pipeline Metrics</h4>
                                    <div className="space-y-2 text-sm">
                                      <div className="flex justify-between">
                                        <span className="text-muted-foreground">Total Applicants</span>
                                        <span className="font-mono">{selectedRunData.metrics.totalApplicants}</span>
                                      </div>
                                      <div className="flex justify-between">
                                        <span className="text-muted-foreground">Processed</span>
                                        <span className="font-mono">{selectedRunData.metrics.processedApplicants}</span>
                                      </div>
                                      <div className="flex justify-between">
                                        <span className="text-muted-foreground">Throughput/hr</span>
                                        <span className="font-mono">{selectedRunData.metrics.throughputPerHour}</span>
                                      </div>
                                      <div className="flex justify-between">
                                        <span className="text-muted-foreground">Agent Utilization</span>
                                        <span className="font-mono">{selectedRunData.metrics.agentUtilization}%</span>
                                      </div>
                                    </div>
                                  </div>
                                  
                                  <div className="space-y-3">
                                    <h4 className="font-semibold text-sm border-b pb-1">Timing Metrics</h4>
                                    <div className="space-y-2 text-sm">
                                      <div className="flex justify-between">
                                        <span className="text-muted-foreground">Median Queue Wait</span>
                                        <span className="font-mono">{selectedRunData.metrics.medianQueueWaitMs}ms</span>
                                      </div>
                                      <div className="flex justify-between">
                                        <span className="text-muted-foreground">P95 Queue Wait</span>
                                        <span className="font-mono">{selectedRunData.metrics.p95QueueWaitMs}ms</span>
                                      </div>
                                      <div className="flex justify-between">
                                        <span className="text-muted-foreground">Median End-to-End</span>
                                        <span className="font-mono">{Math.round(selectedRunData.metrics.medianEndToEndMs / 1000)}s</span>
                                      </div>
                                      <div className="flex justify-between">
                                        <span className="text-muted-foreground">P95 End-to-End</span>
                                        <span className="font-mono">{Math.round(selectedRunData.metrics.p95EndToEndMs / 1000)}s</span>
                                      </div>
                                    </div>
                                  </div>
                                  
                                  <div className="space-y-3">
                                    <h4 className="font-semibold text-sm border-b pb-1">Quality Metrics</h4>
                                    <div className="space-y-2 text-sm">
                                      <div className="flex justify-between">
                                        <span className="text-muted-foreground">SLA 24h Compliance</span>
                                        <span className="font-mono text-green-600">{selectedRunData.metrics.slaCompliance.within24h}%</span>
                                      </div>
                                      <div className="flex justify-between">
                                        <span className="text-muted-foreground">SLA 48h Compliance</span>
                                        <span className="font-mono text-green-600">{selectedRunData.metrics.slaCompliance.within48h}%</span>
                                      </div>
                                      <div className="flex justify-between">
                                        <span className="text-muted-foreground">Invalid Payload Rate</span>
                                        <span className="font-mono">{selectedRunData.metrics.errorRate.invalidPayload}%</span>
                                      </div>
                                      <div className="flex justify-between">
                                        <span className="text-muted-foreground">Duplicate Detection</span>
                                        <span className="font-mono">{selectedRunData.metrics.errorRate.duplicateDetection}%</span>
                                      </div>
                                    </div>
                                  </div>
                                </div>
                                
                                <div className="grid gap-6 md:grid-cols-2">
                                  <div className="space-y-3">
                                    <h4 className="font-semibold text-sm border-b pb-1">Assignment Fairness</h4>
                                    <div className="space-y-2 text-sm">
                                      <div className="flex justify-between">
                                        <span className="text-muted-foreground">Min Tasks/Agent</span>
                                        <span className="font-mono">{selectedRunData.metrics.assignmentFairness.minTasks}</span>
                                      </div>
                                      <div className="flex justify-between">
                                        <span className="text-muted-foreground">Max Tasks/Agent</span>
                                        <span className="font-mono">{selectedRunData.metrics.assignmentFairness.maxTasks}</span>
                                      </div>
                                      <div className="flex justify-between">
                                        <span className="text-muted-foreground">Std Deviation</span>
                                        <span className="font-mono">{selectedRunData.metrics.assignmentFairness.stdDeviation}</span>
                                      </div>
                                    </div>
                                  </div>
                                  
                                  <div className="space-y-3">
                                    <h4 className="font-semibold text-sm border-b pb-1">Delay Breakdown</h4>
                                    <div className="space-y-2 text-sm">
                                      <div className="flex justify-between">
                                        <span className="text-muted-foreground">Avg Queueing Delay</span>
                                        <span className="font-mono">{selectedRunData.metrics.bottleneckAnalysis.queueingDelayMs}ms</span>
                                      </div>
                                      <div className="flex justify-between">
                                        <span className="text-muted-foreground">Avg Assignment Delay</span>
                                        <span className="font-mono">{selectedRunData.metrics.bottleneckAnalysis.assignmentDelayMs}ms</span>
                                      </div>
                                      <div className="flex justify-between">
                                        <span className="text-muted-foreground">Avg Processing Time</span>
                                        <span className="font-mono">{selectedRunData.metrics.bottleneckAnalysis.processingDelayMs}ms</span>
                                      </div>
                                      <div className="flex justify-between">
                                        <span className="text-muted-foreground">Primary Bottleneck</span>
                                        <Badge variant="outline" className="font-mono">{selectedRunData.metrics.bottleneckAnalysis.bottleneck}</Badge>
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              </CardContent>
                            </Card>

                            {selectedRunData.applicants && selectedRunData.applicants.length > 0 && (
                              <Card>
                                <CardHeader>
                                  <CardTitle className="text-base">Sample Applicant Data</CardTitle>
                                  <CardDescription>First 20 synthetic candidates with AI analysis results</CardDescription>
                                </CardHeader>
                                <CardContent>
                                  <div className="overflow-x-auto">
                                    <Table>
                                      <TableHeader>
                                        <TableRow>
                                          <TableHead>ID</TableHead>
                                          <TableHead>Name</TableHead>
                                          <TableHead>Role</TableHead>
                                          <TableHead>Suitability</TableHead>
                                          <TableHead>Bucket</TableHead>
                                          <TableHead>Interview</TableHead>
                                          <TableHead>Job Match</TableHead>
                                          <TableHead>Risk</TableHead>
                                          <TableHead>Status</TableHead>
                                        </TableRow>
                                      </TableHeader>
                                      <TableBody>
                                        {selectedRunData.applicants.slice(0, 20).map((app: any) => {
                                          let riskLevel = "N/A";
                                          try {
                                            const verification = JSON.parse(app.ai_resume_verification || "{}");
                                            riskLevel = verification.overallRisk || "N/A";
                                          } catch {}
                                          return (
                                            <TableRow key={app.applicant_id}>
                                              <TableCell className="font-mono text-xs">{app.applicant_id?.slice(-8)}</TableCell>
                                              <TableCell className="font-medium">{app.name || "—"}</TableCell>
                                              <TableCell>{app.role_preference}</TableCell>
                                              <TableCell>
                                                <Badge variant={app.suitability_type === "strong" ? "default" : app.suitability_type === "borderline" ? "secondary" : "outline"}>
                                                  {app.suitability_type}
                                                </Badge>
                                              </TableCell>
                                              <TableCell>
                                                <Badge variant={app.bucket === "A" ? "default" : app.bucket === "B" ? "secondary" : "outline"}>
                                                  {app.bucket}
                                                </Badge>
                                              </TableCell>
                                              <TableCell className="font-mono">
                                                <span className={app.ai_interview_score >= 70 ? "text-green-600" : app.ai_interview_score >= 50 ? "text-yellow-600" : "text-red-600"}>
                                                  {app.ai_interview_score?.toFixed(1) || "—"}
                                                </span>
                                              </TableCell>
                                              <TableCell className="font-mono">
                                                <span className={app.ai_job_match_score >= 70 ? "text-green-600" : app.ai_job_match_score >= 50 ? "text-yellow-600" : "text-red-600"}>
                                                  {app.ai_job_match_score?.toFixed(1) || "—"}
                                                </span>
                                              </TableCell>
                                              <TableCell>
                                                <Badge variant={riskLevel === "low" ? "default" : riskLevel === "medium" ? "secondary" : "destructive"}>
                                                  {riskLevel}
                                                </Badge>
                                              </TableCell>
                                              <TableCell>
                                                <Badge variant="outline">{app.status}</Badge>
                                              </TableCell>
                                            </TableRow>
                                          );
                                        })}
                                      </TableBody>
                                    </Table>
                                  </div>
                                  {selectedRunData.applicants.length > 20 && (
                                    <p className="text-sm text-muted-foreground mt-3 text-center">
                                      Showing 20 of {selectedRunData.applicants.length} applicants. Export to CSV/JSON for full data.
                                    </p>
                                  )}
                                </CardContent>
                              </Card>
                            )}
                          </>
                        )}
                      </div>
                    ) : (
                      <Card>
                        <CardContent className="py-12 text-center">
                          <FlaskConical className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                          <h3 className="text-lg font-semibold mb-2">Select a Run</h3>
                          <p className="text-muted-foreground">
                            Select a test run from the list or create a new one.
                          </p>
                        </CardContent>
                      </Card>
                    )}
                  </div>
                </div>
              )}

              <Dialog open={showCreateRunModal} onOpenChange={setShowCreateRunModal}>
                <DialogContent className="max-w-lg">
                  <DialogHeader>
                    <DialogTitle>Create Sandbox Test Run</DialogTitle>
                    <DialogDescription>Configure a new efficiency test simulation</DialogDescription>
                  </DialogHeader>
                  <div className="grid gap-4 py-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label>Total Applicants</Label>
                        <Input type="number" value={sandboxApplicants} onChange={(e) => setSandboxApplicants(e.target.value)} data-testid="input-sandbox-applicants" />
                      </div>
                      <div>
                        <Label>Time Window (min)</Label>
                        <Input type="number" value={sandboxTimeWindow} onChange={(e) => setSandboxTimeWindow(e.target.value)} data-testid="input-sandbox-time-window" />
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label>Agent Count</Label>
                        <Input type="number" value={sandboxAgentCount} onChange={(e) => setSandboxAgentCount(e.target.value)} data-testid="input-sandbox-agents" />
                      </div>
                      <div>
                        <Label>Role Family</Label>
                        <Select value={sandboxRoleFamily} onValueChange={setSandboxRoleFamily}>
                          <SelectTrigger data-testid="select-sandbox-role-family"><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="Software">Software</SelectItem>
                            <SelectItem value="MedTech">MedTech</SelectItem>
                            <SelectItem value="Trades">Trades</SelectItem>
                            <SelectItem value="Corporate">Corporate</SelectItem>
                            <SelectItem value="Mixed">Mixed</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label>Processing Model</Label>
                        <Select value={sandboxProcessingModel} onValueChange={setSandboxProcessingModel}>
                          <SelectTrigger data-testid="select-sandbox-processing"><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="auto">Auto (Fast)</SelectItem>
                            <SelectItem value="human_sim">Human Sim</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label>Assignment Policy</Label>
                        <Select value={sandboxAssignmentPolicy} onValueChange={setSandboxAssignmentPolicy}>
                          <SelectTrigger data-testid="select-sandbox-assignment"><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="round_robin">Round Robin</SelectItem>
                            <SelectItem value="load_based">Load Based</SelectItem>
                            <SelectItem value="priority_bucket">Priority Bucket</SelectItem>
                            <SelectItem value="skill_matching">Skill Matching</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                    <div className="grid grid-cols-3 gap-4">
                      <div>
                        <Label>Strong %</Label>
                        <Input type="number" value={sandboxStrongPct} onChange={(e) => setSandboxStrongPct(e.target.value)} data-testid="input-sandbox-strong" />
                      </div>
                      <div>
                        <Label>Borderline %</Label>
                        <Input type="number" value={sandboxBorderlinePct} onChange={(e) => setSandboxBorderlinePct(e.target.value)} data-testid="input-sandbox-borderline" />
                      </div>
                      <div>
                        <Label>Irrelevant %</Label>
                        <Input type="number" value={String(100 - parseInt(sandboxStrongPct) - parseInt(sandboxBorderlinePct))} disabled className="bg-muted" />
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label>Duplicate Rate %</Label>
                        <Input type="number" value={sandboxDuplicateRate} onChange={(e) => setSandboxDuplicateRate(e.target.value)} data-testid="input-sandbox-duplicates" />
                      </div>
                      <div>
                        <Label>Invalid Data Rate %</Label>
                        <Input type="number" value={sandboxInvalidRate} onChange={(e) => setSandboxInvalidRate(e.target.value)} data-testid="input-sandbox-invalid" />
                      </div>
                    </div>
                  </div>
                  <DialogFooter>
                    <Button variant="outline" onClick={() => setShowCreateRunModal(false)}>Cancel</Button>
                    <Button onClick={handleCreateSandboxRun} disabled={createSandboxRunMutation.isPending} data-testid="btn-confirm-create-sandbox">
                      {createSandboxRunMutation.isPending ? 'Creating...' : 'Create Run'}
                    </Button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>
            </div>
          )}

          {activeTab === "comms" && (
            <div className="space-y-6" data-testid="comm-health-panel">
              <div>
                <h1 className="text-2xl font-bold">Communication Health</h1>
                <p className="text-muted-foreground mt-1">Monitor email and SMS setup across all workspaces</p>
              </div>

              <div className="flex gap-6 text-sm">
                <div className="flex items-center gap-2">
                  <span className="w-3 h-3 rounded-full bg-green-500 inline-block" />
                  <span className="text-muted-foreground">Healthy</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="w-3 h-3 rounded-full bg-amber-500 inline-block" />
                  <span className="text-muted-foreground">Email configured but no recent sends</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="w-3 h-3 rounded-full bg-red-500 inline-block" />
                  <span className="text-muted-foreground">Email not configured with active candidates</span>
                </div>
              </div>

              <Card data-testid="comm-health-table-card">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Status</TableHead>
                      <TableHead>Workspace</TableHead>
                      <TableHead>Agent</TableHead>
                      <TableHead>Tier</TableHead>
                      <TableHead>Email</TableHead>
                      <TableHead>SMS</TableHead>
                      <TableHead>Active Candidates</TableHead>
                      <TableHead>Last Email</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {!commHealthData && (
                      <TableRow>
                        <TableCell colSpan={8} className="text-center text-muted-foreground py-8">
                          Loading...
                        </TableCell>
                      </TableRow>
                    )}
                    {commHealthData && commHealthData.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={8} className="text-center text-muted-foreground py-8">
                          No workspaces found
                        </TableCell>
                      </TableRow>
                    )}
                    {(commHealthData || []).map((row: any) => (
                      <TableRow
                        key={row.workspace_id}
                        data-testid={`comm-health-row-${row.workspace_id}`}
                      >
                        <TableCell>
                          <span
                            className={`w-3 h-3 rounded-full inline-block ${
                              row.status_flag === "red"
                                ? "bg-red-500"
                                : row.status_flag === "amber"
                                ? "bg-amber-500"
                                : "bg-green-500"
                            }`}
                            title={
                              row.status_flag === "red"
                                ? "Email not configured — candidates cannot be notified"
                                : row.status_flag === "amber"
                                ? "Email configured but no recent sends"
                                : "Healthy"
                            }
                          />
                        </TableCell>
                        <TableCell className="font-medium">{row.workspace_name}</TableCell>
                        <TableCell className="text-muted-foreground">{row.recruiter_name}</TableCell>
                        <TableCell>
                          <Badge variant="outline" className="text-xs">{row.tier}</Badge>
                        </TableCell>
                        <TableCell>
                          {row.email_configured ? (
                            <CheckCircle2 className="w-4 h-4 text-green-500" />
                          ) : (
                            <AlertTriangle className="w-4 h-4 text-amber-500" />
                          )}
                        </TableCell>
                        <TableCell>
                          {row.sms_configured ? (
                            <CheckCircle2 className="w-4 h-4 text-green-500" />
                          ) : (
                            <span className="text-muted-foreground text-xs">—</span>
                          )}
                        </TableCell>
                        <TableCell>{row.active_candidate_count}</TableCell>
                        <TableCell className="text-muted-foreground text-sm">
                          {row.last_email_sent
                            ? new Date(row.last_email_sent).toLocaleDateString()
                            : "Never"}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </Card>
            </div>
          )}

          {activeTab === "feedback" && (
            <div className="space-y-6" data-testid="feedback-panel">
              <div className="flex items-center justify-between flex-wrap gap-3">
                <div>
                  <h1 className="text-2xl font-bold">Assessment Feedback</h1>
                  <p className="text-muted-foreground text-sm mt-1">All recruiter and candidate feedback submissions</p>
                </div>
                <div className="flex items-center gap-2">
                  <Select value={feedbackTypeFilter} onValueChange={(v) => { setFeedbackTypeFilter(v); setFeedbackPage(1); }}>
                    <SelectTrigger className="w-36 h-8 text-xs" data-testid="select-feedback-type">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All types</SelectItem>
                      <SelectItem value="recruiter">Recruiter</SelectItem>
                      <SelectItem value="candidate">Candidate</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Summary cards */}
              <div className="grid gap-4 sm:grid-cols-3">
                <Card data-testid="card-feedback-total">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm font-medium text-muted-foreground">Total Responses</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-3xl font-bold" data-testid="stat-feedback-total">{feedbackData?.total ?? "—"}</p>
                    <p className="text-xs text-muted-foreground mt-1">across all responses</p>
                  </CardContent>
                </Card>
                <Card data-testid="card-feedback-avg">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm font-medium text-muted-foreground">Average Rating</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="flex items-center gap-1.5">
                      <Star className="w-5 h-5 fill-yellow-400 text-yellow-400" />
                      <p className="text-3xl font-bold" data-testid="stat-feedback-avg">
                        {feedbackData?.averageRating != null ? feedbackData.averageRating.toFixed(1) : "—"}
                      </p>
                    </div>
                    <p className="text-xs text-muted-foreground mt-1">across all responses</p>
                  </CardContent>
                </Card>
                <Card data-testid="card-feedback-recommend">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm font-medium text-muted-foreground">Would Recommend</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-3xl font-bold" data-testid="stat-feedback-recommend">
                      {feedbackData != null ? `${feedbackData.wouldRecommendPercent}%` : "—"}
                    </p>
                    <p className="text-xs text-muted-foreground mt-1">across all responses</p>
                  </CardContent>
                </Card>
              </div>

              {/* Feedback table */}
              <Card>
                <Table data-testid="table-feedback">
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-24">Type</TableHead>
                      <TableHead>Candidate</TableHead>
                      <TableHead>Workspace</TableHead>
                      <TableHead>Submitted by</TableHead>
                      <TableHead className="w-28">Rating</TableHead>
                      <TableHead>Comments</TableHead>
                      <TableHead className="w-36">Date</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {feedbackLoading ? (
                      <TableRow>
                        <TableCell colSpan={7} className="text-center text-muted-foreground py-10 text-sm">Loading feedback...</TableCell>
                      </TableRow>
                    ) : !feedbackData?.feedback?.length ? (
                      <TableRow>
                        <TableCell colSpan={7} className="text-center text-muted-foreground py-10 text-sm">No feedback submitted yet.</TableCell>
                      </TableRow>
                    ) : feedbackData.feedback.map((f: any) => (
                      <TableRow key={f.id} data-testid={`row-feedback-${f.id}`}>
                        <TableCell>
                          <Badge variant="outline" className={f.type === "recruiter" ? "border-blue-400 text-blue-600" : "border-violet-400 text-violet-600"} data-testid={`badge-feedback-type-${f.id}`}>
                            {f.type === "recruiter" ? "Recruiter" : "Candidate"}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-sm" data-testid={`text-feedback-candidate-${f.id}`}>{f.candidate_name ?? <span className="text-muted-foreground">—</span>}</TableCell>
                        <TableCell className="text-sm text-muted-foreground" data-testid={`text-feedback-workspace-${f.id}`}>{f.workspace_name ?? "—"}</TableCell>
                        <TableCell className="text-sm text-muted-foreground" data-testid={`text-feedback-submitter-${f.id}`}>{f.submitted_by_name ?? <span className="italic">Candidate</span>}</TableCell>
                        <TableCell data-testid={`text-feedback-rating-${f.id}`}>
                          <div className="flex items-center gap-0.5">
                            {[1,2,3,4,5].map(n => (
                              <Star key={n} className={`w-3.5 h-3.5 ${n <= f.rating ? "fill-yellow-400 text-yellow-400" : "text-muted-foreground/20"}`} />
                            ))}
                          </div>
                        </TableCell>
                        <TableCell className="text-sm max-w-xs" data-testid={`text-feedback-comments-${f.id}`}>
                          {f.comments ? <span className="line-clamp-2">{f.comments}</span> : <span className="text-muted-foreground text-xs italic">No comment</span>}
                        </TableCell>
                        <TableCell className="text-xs text-muted-foreground" data-testid={`text-feedback-date-${f.id}`}>
                          {new Date(f.submitted_at).toLocaleDateString("en-NZ", { day: "numeric", month: "short", year: "numeric" })}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
                {/* Pagination */}
                {(feedbackData?.total ?? 0) > (feedbackData?.limit ?? 50) && (
                  <div className="flex items-center justify-between px-4 py-3 border-t">
                    <p className="text-xs text-muted-foreground">Showing {((feedbackPage - 1) * (feedbackData?.limit ?? 50)) + 1}–{Math.min(feedbackPage * (feedbackData?.limit ?? 50), feedbackData?.total ?? 0)} of {feedbackData?.total}</p>
                    <div className="flex gap-2">
                      <Button size="sm" variant="outline" disabled={feedbackPage <= 1} onClick={() => setFeedbackPage(p => p - 1)} data-testid="button-feedback-prev">Previous</Button>
                      <Button size="sm" variant="outline" disabled={feedbackPage * (feedbackData?.limit ?? 50) >= (feedbackData?.total ?? 0)} onClick={() => setFeedbackPage(p => p + 1)} data-testid="button-feedback-next">Next</Button>
                    </div>
                  </div>
                )}
              </Card>
            </div>
          )}

          {activeTab === "flagged" && (
            <div className="space-y-6" data-testid="flagged-panel">
              <div className="flex items-center justify-between">
                <div>
                  <h1 className="text-2xl font-bold">Flagged Accounts</h1>
                  <p className="text-muted-foreground text-sm mt-1">Workspaces flagged by abuse-prevention rules</p>
                </div>
                <Button size="sm" variant="outline" onClick={() => refetchFlagged()} data-testid="button-refresh-flagged">
                  Refresh
                </Button>
              </div>

              <Card>
                {flaggedLoading ? (
                  <div className="flex items-center justify-center py-16">
                    <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
                  </div>
                ) : !flaggedData?.accounts?.length ? (
                  <div className="flex flex-col items-center justify-center py-16 text-center">
                    <BadgeCheck className="w-10 h-10 text-green-400 mb-3" />
                    <p className="text-sm text-muted-foreground">No flagged accounts at this time.</p>
                  </div>
                ) : (
                  <Table data-testid="table-flagged">
                    <TableHeader>
                      <TableRow>
                        <TableHead>Email</TableHead>
                        <TableHead>Workspace</TableHead>
                        <TableHead>Flag Reason</TableHead>
                        <TableHead>IP Address</TableHead>
                        <TableHead>Registered</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead className="text-right">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {flaggedData.accounts.map((acct: any) => (
                        <TableRow key={acct.id} data-testid={`row-flagged-${acct.id}`}>
                          <TableCell className="text-sm font-medium" data-testid={`text-flagged-email-${acct.id}`}>{acct.email}</TableCell>
                          <TableCell className="text-sm text-muted-foreground" data-testid={`text-flagged-workspace-${acct.id}`}>{acct.workspace_name}</TableCell>
                          <TableCell>
                            <Badge variant="outline" className="border-amber-400 text-amber-500 text-xs" data-testid={`badge-flagged-reason-${acct.id}`}>
                              {acct.flagged_reason?.replace(/_/g, " ") ?? "flagged"}
                            </Badge>
                          </TableCell>
                          <TableCell className="text-xs text-muted-foreground font-mono" data-testid={`text-flagged-ip-${acct.id}`}>{acct.registration_ip ?? "—"}</TableCell>
                          <TableCell className="text-xs text-muted-foreground" data-testid={`text-flagged-date-${acct.id}`}>
                            {acct.created_at ? new Date(acct.created_at).toLocaleDateString("en-NZ", { day: "numeric", month: "short", year: "numeric" }) : "—"}
                          </TableCell>
                          <TableCell>
                            {acct.blocked_at ? (
                              <Badge variant="destructive" className="text-xs" data-testid={`badge-flagged-status-${acct.id}`}>Blocked</Badge>
                            ) : (
                              <Badge variant="outline" className="border-amber-400 text-amber-500 text-xs" data-testid={`badge-flagged-status-${acct.id}`}>Flagged</Badge>
                            )}
                          </TableCell>
                          <TableCell className="text-right">
                            <div className="flex items-center justify-end gap-2">
                              {!acct.blocked_at && (
                                <Button size="sm" variant="destructive" className="h-7 text-xs gap-1"
                                  onClick={() => blockWorkspaceMutation.mutate(acct.id)}
                                  disabled={blockWorkspaceMutation.isPending}
                                  data-testid={`button-block-${acct.id}`}>
                                  <Ban className="w-3 h-3" /> Block
                                </Button>
                              )}
                              <Button size="sm" variant="outline" className="h-7 text-xs gap-1 border-green-500 text-green-400 hover:bg-green-950"
                                onClick={() => approveWorkspaceMutation.mutate(acct.id)}
                                disabled={approveWorkspaceMutation.isPending}
                                data-testid={`button-approve-${acct.id}`}>
                                <BadgeCheck className="w-3 h-3" /> Approve
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </Card>
            </div>
          )}

          {activeTab === "settings" && (
            <div className="space-y-6">
              <h1 className="text-2xl font-bold">Settings</h1>

              <div className="grid gap-6 md:grid-cols-2">
                <Card>
                  <CardHeader>
                    <CardTitle>Reporting Metrics</CardTitle>
                    <CardDescription>Last 30 days performance</CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="flex justify-between">
                      <span>Completed Interviews</span>
                      <span className="font-bold">{metrics?.completedInterviews || 0}</span>
                    </div>
                    <div className="flex justify-between">
                      <span>Placements</span>
                      <span className="font-bold">{metrics?.placements || 0}</span>
                    </div>
                    <div className="flex justify-between">
                      <span>SLA Breaches</span>
                      <span className="font-bold text-red-600">{metrics?.slaBreaches || 0}</span>
                    </div>
                    <div className="flex justify-between">
                      <span>SLA Attainment</span>
                      <span className="font-bold text-green-600">{metrics?.slaAttainment || 100}%</span>
                    </div>
                    <Button className="w-full mt-4" variant="outline" onClick={handleExportMetrics} data-testid="button-export-metrics">
                      <Download className="mr-2 h-4 w-4" />
                      Export CSV
                    </Button>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader>
                    <CardTitle>SLA Configurations</CardTitle>
                    <CardDescription>Default timing rules</CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="flex justify-between">
                      <span>Intake Deadline</span>
                      <span className="font-medium">24 hours</span>
                    </div>
                    <div className="flex justify-between">
                      <span>First Outreach</span>
                      <span className="font-medium">48 hours</span>
                    </div>
                    <div className="flex justify-between">
                      <span>Shortlist Delivery</span>
                      <span className="font-medium">7 days</span>
                    </div>
                    <div className="flex justify-between">
                      <span>Stale Threshold</span>
                      <span className="font-medium">14 days</span>
                    </div>
                    <Button className="w-full mt-4" variant="outline" onClick={() => setShowEditSlaModal(true)} data-testid="button-edit-sla">
                      Edit SLA Rules
                    </Button>
                  </CardContent>
                </Card>
              </div>
            </div>
          )}
        </main>
      </div>

      {/* Add Team Modal */}
      <Dialog open={showAddTeamModal} onOpenChange={setShowAddTeamModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Create New Team</DialogTitle>
            <DialogDescription>Add a new team/pod to organize agents</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="team-name">Team Name</Label>
              <Input 
                id="team-name" 
                value={newTeamName} 
                onChange={(e) => setNewTeamName(e.target.value)}
                placeholder="e.g., Healthcare Pod"
                data-testid="input-team-name"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="team-description">Description</Label>
              <Textarea 
                id="team-description" 
                value={newTeamDescription} 
                onChange={(e) => setNewTeamDescription(e.target.value)}
                placeholder="Brief description of this team's focus"
                data-testid="input-team-description"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="team-capacity">Max Capacity</Label>
              <Input 
                id="team-capacity" 
                type="number"
                value={newTeamCapacity} 
                onChange={(e) => setNewTeamCapacity(e.target.value)}
                placeholder="10"
                data-testid="input-team-capacity"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddTeamModal(false)}>Cancel</Button>
            <Button onClick={handleCreateTeam} disabled={createTeamMutation.isPending} data-testid="button-submit-team">
              {createTeamMutation.isPending ? "Creating..." : "Create Team"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Assign Task Modal */}
      <Dialog open={showAssignModal} onOpenChange={setShowAssignModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Assign Task</DialogTitle>
            <DialogDescription>
              Assign "{selectedQueueItem?.title}" to an agent
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Select Agent</Label>
              <Select value={selectedAgentForAssign} onValueChange={setSelectedAgentForAssign}>
                <SelectTrigger data-testid="select-agent-assign">
                  <SelectValue placeholder="Choose an agent" />
                </SelectTrigger>
                <SelectContent>
                  {agents?.map(agent => (
                    <SelectItem key={agent.userId} value={agent.userId.toString()}>
                      {agent.fullName} ({agent.teamName || "No team"})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAssignModal(false)}>Cancel</Button>
            <Button onClick={handleAssignTask} disabled={assignQueueItemMutation.isPending} data-testid="button-confirm-assign">
              {assignQueueItemMutation.isPending ? "Assigning..." : "Assign Task"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit SLA Modal */}
      <Dialog open={showEditSlaModal} onOpenChange={setShowEditSlaModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit SLA Configuration</DialogTitle>
            <DialogDescription>Configure default SLA timing rules</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="sla-intake">Intake Deadline (hours)</Label>
                <Input 
                  id="sla-intake" 
                  type="number"
                  value={slaIntakeHours} 
                  onChange={(e) => setSlaIntakeHours(e.target.value)}
                  data-testid="input-sla-intake"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="sla-outreach">First Outreach (hours)</Label>
                <Input 
                  id="sla-outreach" 
                  type="number"
                  value={slaOutreachHours} 
                  onChange={(e) => setSlaOutreachHours(e.target.value)}
                  data-testid="input-sla-outreach"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="sla-shortlist">Shortlist Delivery (days)</Label>
                <Input 
                  id="sla-shortlist" 
                  type="number"
                  value={slaShortlistDays} 
                  onChange={(e) => setSlaShortlistDays(e.target.value)}
                  data-testid="input-sla-shortlist"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="sla-stale">Stale Threshold (days)</Label>
                <Input 
                  id="sla-stale" 
                  type="number"
                  value={slaStaleDays} 
                  onChange={(e) => setSlaStaleDays(e.target.value)}
                  data-testid="input-sla-stale"
                />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEditSlaModal(false)}>Cancel</Button>
            <Button 
              onClick={() => {
                toast({ title: "SLA Updated", description: "Default SLA configuration has been saved." });
                setShowEditSlaModal(false);
              }} 
              data-testid="button-save-sla"
            >
              Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Notifications Modal */}
      <Dialog open={showNotificationsModal} onOpenChange={setShowNotificationsModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Notifications</DialogTitle>
            <DialogDescription>
              {unreadNotifications.length > 0 
                ? `${unreadNotifications.length} unread notification(s)` 
                : "No unread notifications"}
            </DialogDescription>
          </DialogHeader>
          <div className="max-h-80 overflow-y-auto space-y-3">
            {notifications?.length === 0 && (
              <p className="text-center text-muted-foreground py-4">No notifications yet</p>
            )}
            {notifications?.map(notification => (
              <div 
                key={notification.id} 
                className={`p-3 rounded-lg border ${!notification.isRead ? "bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-800" : "bg-muted/50"}`}
                data-testid={`notification-${notification.id}`}
              >
                <div className="flex items-start justify-between gap-2">
                  <div>
                    <p className="font-medium text-sm">{notification.title}</p>
                    <p className="text-sm text-muted-foreground">{notification.message}</p>
                    <p className="text-xs text-muted-foreground mt-1">
                      {new Date(notification.createdAt).toLocaleString()}
                    </p>
                  </div>
                  {!notification.isRead && (
                    <Button 
                      size="sm" 
                      variant="ghost" 
                      onClick={() => markNotificationReadMutation.mutate(notification.id)}
                      data-testid={`button-mark-read-${notification.id}`}
                    >
                      <X className="h-4 w-4" />
                    </Button>
                  )}
                </div>
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>

      {/* Generate API Key Modal */}
      <Dialog open={showGenerateKeyModal} onOpenChange={setShowGenerateKeyModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Generate API Key</DialogTitle>
            <DialogDescription>Create a new API key for external integrations</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="key-name">Key Name</Label>
              <Input 
                id="key-name" 
                placeholder="e.g., Dossier Sync Production"
                value={newKeyName} 
                onChange={(e) => setNewKeyName(e.target.value)}
                data-testid="input-key-name"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="key-scope">Scope</Label>
              <Select value={newKeyScope} onValueChange={setNewKeyScope}>
                <SelectTrigger id="key-scope" data-testid="select-key-scope">
                  <SelectValue placeholder="Select scope" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="dossier_sync">Dossier Sync</SelectItem>
                  <SelectItem value="full_access">Full Access</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="key-expiry">Expiration</Label>
              <Select value={newKeyExpiry} onValueChange={setNewKeyExpiry}>
                <SelectTrigger id="key-expiry" data-testid="select-key-expiry">
                  <SelectValue placeholder="Select expiration" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="never">Never expires</SelectItem>
                  <SelectItem value="30">30 days</SelectItem>
                  <SelectItem value="90">90 days</SelectItem>
                  <SelectItem value="365">1 year</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowGenerateKeyModal(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleGenerateKey}
              disabled={generateApiKeyMutation.isPending}
              data-testid="btn-confirm-generate-key"
            >
              {generateApiKeyMutation.isPending ? "Generating..." : "Generate Key"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
